import Head from 'next/head';
import Link from 'next/link';
import dayjs from 'dayjs';
import { PortableText } from '@portabletext/react';
import type { PortableTextComponents } from '@portabletext/react';

import type { LegalPage as LegalPageData } from '@lib/sanity';
import { WEBSITE_NAME } from '@lib/constants';

const slugifyHeading = (children: unknown): string | undefined => {
  if (!Array.isArray(children)) return undefined;
  const text = children
    .map((node) => (typeof node === 'string' ? node : ''))
    .join('')
    .trim();
  if (!text) return undefined;
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const portableTextComponents: PortableTextComponents = {
  block: {
    normal: ({ children }) => (
      <p className="font-open-sans text-base leading-relaxed text-neutral-700 mb-5">
        {children}
      </p>
    ),
    h2: ({ children }) => (
      <h2
        id={slugifyHeading(children)}
        className="font-sans text-2xl font-bold tracking-tight text-neutral-800 mt-16 mb-5 scroll-mt-24"
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        id={slugifyHeading(children)}
        className="font-sans text-xl font-bold text-neutral-800 mt-12 mb-4 scroll-mt-24"
      >
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4
        id={slugifyHeading(children)}
        className="font-sans text-lg font-semibold text-neutral-800 mt-10 mb-3 scroll-mt-24"
      >
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <h5
        id={slugifyHeading(children)}
        className="font-sans text-base font-semibold text-neutral-800 mt-8 mb-2 scroll-mt-24"
      >
        {children}
      </h5>
    )
  },
  list: {
    bullet: ({ children }) => (
      <ul className="font-open-sans list-disc pl-6 mb-5 space-y-2 text-neutral-700">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="font-open-sans list-decimal pl-6 mb-5 space-y-2 text-neutral-700">
        {children}
      </ol>
    )
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="text-base leading-relaxed marker:text-neutral-400">
        {children}
      </li>
    ),
    number: ({ children }) => (
      <li className="text-base leading-relaxed marker:text-neutral-400">
        {children}
      </li>
    )
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold text-neutral-800">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    link: ({ value, children }) => {
      const href = value?.href ?? '';
      const isExternal = /^https?:\/\//i.test(href);
      return (
        <a
          href={href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="text-primary-700 underline decoration-primary-200 underline-offset-4 transition-colors hover:decoration-primary-700"
        >
          {children}
        </a>
      );
    }
  }
};

type LegalPageProps = { page: LegalPageData };

const LegalPage = ({ page }: LegalPageProps) => {
  const metaTitle = page.seo?.metaTitle || `${page.title} | ${WEBSITE_NAME}`;
  const ogImageUrl = page.seo?.ogImage?.asset?.url;

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        {page.seo?.metaDescription && (
          <meta name="description" content={page.seo.metaDescription} />
        )}
        <meta property="og:title" content={metaTitle} key="og:title" />
        {page.seo?.metaDescription && (
          <meta
            property="og:description"
            content={page.seo.metaDescription}
            key="og:description"
          />
        )}
        {ogImageUrl && (
          <meta property="og:image" content={ogImageUrl} key="og:image" />
        )}
        {page.seo?.canonicalUrl && (
          <link rel="canonical" href={page.seo.canonicalUrl} key="canonical" />
        )}
        {page.seo?.isHiddenFromSearch && (
          <meta name="robots" content="noindex,nofollow" />
        )}
      </Head>

      <main className="min-h-screen bg-white">
        <article className="mx-auto max-w-3xl px-6 pt-12 pb-24 sm:px-8 sm:pt-20 sm:pb-32">
          <Link
            href="/"
            className="inline-flex items-center gap-2 font-open-sans text-sm text-neutral-500 transition-colors hover:text-primary-700"
          >
            <span aria-hidden="true">&larr;</span>
            <span>Fantasy Football Hub</span>
          </Link>

          <header className="mt-10 mb-12 pb-10 border-b border-neutral-200">
            <p className="font-open-sans text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-4">
              Legal
            </p>
            <h1 className="font-sans text-4xl sm:text-5xl font-bold leading-tight text-neutral-800">
              {page.title}
            </h1>
            {page.effectiveDate && (
              <p className="font-open-sans text-sm text-neutral-500 mt-5">
                Last updated{' '}
                <time dateTime={page.effectiveDate}>
                  {dayjs(page.effectiveDate).format('D MMMM YYYY')}
                </time>
              </p>
            )}
          </header>

          <div>
            <PortableText
              value={page.body}
              components={portableTextComponents}
            />
          </div>
        </article>
      </main>
    </>
  );
};

export default LegalPage;
